import { Grid, TextField } from '@mui/material'
import React from 'react'

import { OfficeFormData } from '../../types/ServiceTypes'

interface OfficeFormFieldsProps {
  formData: OfficeFormData
  onChange: (field: keyof OfficeFormData, value: string) => void
  disabled?: boolean
}

/**
 * OfficeFormFields - Component for office form input fields
 *
 * Handles all form field rendering including office name (required),
 * description, address, and contact information (all optional).
 *
 * Requirements: 1.1, 2.1, 2.2, 2.3, 2.4, 6.3, 6.4
 */
export default function OfficeFormFields({
  formData,
  onChange,
  disabled = false
}: OfficeFormFieldsProps) {
  const handleFieldChange =
    (field: keyof OfficeFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, event.target.value)
    }

  const fieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: disabled ? 'none' : 'translateY(-1px)',
        boxShadow: disabled ? 'none' : '0 4px 12px rgba(0,0,0,0.1)'
      },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(0,0,0,0.04)',
        '& fieldset': {
          borderColor: 'rgba(0,0,0,0.12)'
        }
      }
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: 'rgba(0,0,0,0.38)'
    },
    '& .MuiFormHelperText-root.Mui-disabled': {
      color: 'rgba(0,0,0,0.38)'
    }
  }

  return (
    <Grid container spacing={3}>
      {/* Office Name Field - Required */}
      <Grid item xs={12}>
        <TextField
          autoComplete='organization'
          value={formData.name}
          onChange={handleFieldChange('name')}
          name='officeName'
          required
          fullWidth
          id='officeName'
          label='Office Name'
          autoFocus
          disabled={disabled}
          inputProps={{
            'aria-describedby': 'office-name-help',
            maxLength: 100
          }}
          helperText='Enter a unique name for your office (2-100 characters)'
          sx={fieldStyles}
        />
      </Grid>

      {/* Office Description Field - Optional */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          id='officeDescription'
          label='Description (optional)'
          name='officeDescription'
          value={formData.description || ''}
          onChange={handleFieldChange('description')}
          multiline
          rows={3}
          disabled={disabled}
          inputProps={{
            'aria-describedby': 'office-description-help',
            maxLength: 500
          }}
          helperText='Describe your office space, amenities, or any other relevant information'
          sx={fieldStyles}
        />
      </Grid>

      {/* Office Address Field - Optional */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id='officeAddress'
          label='Address (optional)'
          name='officeAddress'
          value={formData.address || ''}
          onChange={handleFieldChange('address')}
          autoComplete='street-address'
          disabled={disabled}
          inputProps={{
            'aria-describedby': 'office-address-help'
          }}
          helperText='Physical address of the office'
          sx={fieldStyles}
        />
      </Grid>

      {/* Office Contact Information Field - Optional */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          id='officeContactInfo'
          label='Contact Information (optional)'
          name='officeContactInfo'
          value={formData.contactInfo || ''}
          onChange={handleFieldChange('contactInfo')}
          disabled={disabled}
          inputProps={{
            'aria-describedby': 'office-contact-help'
          }}
          helperText='Phone number, email, or other contact details'
          sx={fieldStyles}
        />
      </Grid>
    </Grid>
  )
}
